import { callBackendCommand, firebaseFunctionCommands } from '@/backend';
import {
    linkGoogleProviderToCurrentAuth,
    refreshCurrentAuthToken,
    refreshCurrentAuthProfile,
    getCurrentFirebaseAuthUid,
    signInWithGoogleProviderForAuthorizationLink,
    logout,
} from './auth-service';

export interface AuthorizationLinkStatus {
    authUid: string;
    canonicalUserId: string;
    isAlias?: boolean;
    currentProviderIds: string[];
    googleLinked: boolean;
}

function isFirebaseAuthCode(error: unknown, codes: string[]): boolean {
    if (typeof error !== 'object' || error === null || !('code' in error)) return false;
    return typeof error.code === 'string' && codes.includes(error.code);
}

function isGoogleCredentialInUseError(error: unknown): boolean {
    return isFirebaseAuthCode(error, [
        'auth/account-exists-with-different-credential',
        'auth/credential-already-in-use',
        'auth/email-already-in-use',
    ]);
}

function isProviderAlreadyLinkedError(error: unknown): boolean {
    return isFirebaseAuthCode(error, ['auth/provider-already-linked']);
}

export function getAuthorizationLinkStatus(): Promise<AuthorizationLinkStatus> {
    return callBackendCommand(firebaseFunctionCommands.getAuthorizationLinkStatus, {});
}

export async function connectGoogleAuthorization(): Promise<AuthorizationLinkStatus> {
    try {
        await linkGoogleProviderToCurrentAuth();
        return getAuthorizationLinkStatus();
    } catch (error: unknown) {
        if (isProviderAlreadyLinkedError(error)) {
            return getAuthorizationLinkStatus();
        }
        if (!isGoogleCredentialInUseError(error)) {
            throw error;
        }
    }

    const originalAuthUid = getCurrentFirebaseAuthUid();
    const session = await callBackendCommand(firebaseFunctionCommands.createAuthorizationLinkSession, {});
    try {
        await signInWithGoogleProviderForAuthorizationLink();
        await callBackendCommand(firebaseFunctionCommands.completeAuthorizationLinkSession, {
            sessionId: session.sessionId,
            secret: session.secret,
        });
        await refreshCurrentAuthToken();
        await refreshCurrentAuthProfile();
    } catch (error: unknown) {
        const currentAuthUid = getCurrentFirebaseAuthUid();
        if (currentAuthUid && currentAuthUid !== originalAuthUid) {
            await logout();
            await refreshCurrentAuthProfile();
        }
        throw error;
    }

    return getAuthorizationLinkStatus();
}
